import React, { useState, useMemo, useEffect } from 'react';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Check, X } from '@phosphor-icons/react';

/**
 * Multi-select simple avec pills. Props :
 *   options: [{value,label}], values: string[], onChange(values), testId
 */
export function MultiSelectChips({ options, values = [], onChange, placeholder = 'Choisir…', testId = 'multi', disabled = false }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);
  const filtered = useMemo(
    () => options.filter(o => !search || o.label.toLowerCase().includes(search.toLowerCase())),
    [options, search]
  );
  const toggle = (v) => {
    if (disabled) return;
    if (values.includes(v)) onChange(values.filter(x => x !== v));
    else onChange([...values, v]);
  };
  return (
    <div className="space-y-2" data-testid={testId}>
      <div
        role="button" tabIndex={disabled ? -1 : 0} aria-disabled={disabled} aria-expanded={open && !disabled}
        aria-label={placeholder} aria-controls={`${testId}-options`}
        data-testid={`${testId}-trigger`}
        className={`flex flex-wrap gap-1.5 min-h-[36px] p-2 border border-[#E8E6E1] rounded-md ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-text'}`}
        onClick={() => { if (!disabled) setOpen(true); }}
        onKeyDown={(e) => {
          if (!disabled && e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(true); }
        }}
      >
        {values.length === 0 && (
          <span className="text-sm text-[#5E6D66]">{placeholder}</span>
        )}
        {values.map(v => {
          const opt = options.find(o => o.value === v);
          return (
            <Badge key={v} variant="outline" className="border-[#2D5A4C]/40 text-[#2D5A4C] gap-1.5" data-testid={`${testId}-chip-${v}`}>
              {opt?.label || v}
              <button
                type="button"
                disabled={disabled}
                data-testid={`${testId}-remove-${v}`}
                className="hover:bg-[#2D5A4C]/10 rounded-sm"
                onClick={(e) => { e.stopPropagation(); toggle(v); }}
                aria-label={`Retirer ${opt?.label || v}`}
              >
                <X size={12} />
              </button>
            </Badge>
          );
        })}
      </div>
      {open && !disabled && (
        <div id={`${testId}-options`} data-testid={`${testId}-options`} className="border border-[#E8E6E1] rounded-md bg-white shadow-sm">
          <div className="p-2 border-b border-[#E8E6E1]">
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher…"
              className="h-8 text-sm"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="max-h-[220px] overflow-y-auto">
            {filtered.map(o => {
              const active = values.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => toggle(o.value)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-[#F9F8F5] ${active ? 'bg-[#F9F8F5] text-[#2D5A4C] font-medium' : ''}`}
                  data-testid={`${testId}-opt-${o.value}`}
                >
                  <span>{o.label}</span>
                  {active && <Check size={14} className="text-[#2D5A4C]" />}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div data-testid={`${testId}-empty`} className="px-3 py-4 text-sm text-[#5E6D66] text-center">Aucun résultat</div>
            )}
          </div>
          <div className="p-2 border-t border-[#E8E6E1] flex justify-end">
            <button type="button" className="text-xs text-[#5E6D66] hover:text-[#2D5A4C]" onClick={() => { setOpen(false); setSearch(''); }} data-testid={`${testId}-close`}>
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MultiSelectChips;
